import { rgba } from 'polished'

import { contentStyle, width, maxWidth, padding } from 'common.styles'

export const postContentStyle = theme => ({
  ...contentStyle(),
  ...maxWidth,
  '& > p:first-child': {
    marginTop: 6,
  },
  '& .image-grid-wrapper': {
    marginInline: 'auto',
    marginBlockStart: 6,
    marginBlockEnd: 7.5,
    ...width(),
  },
  '& .image-grid': {
    display: 'grid',
    gap: 1.5,
    alignItems: 'stretch',
    '&.image-grid-half, &.image-grid-third, &.image-grid-forth': {
      justifyContent: 'center'
    },
    '&.image-grid-half': {
      gridTemplateColumns: 'repeat(1, 0.5fr)',
    },
    '&.image-grid-third': {
      gridTemplateColumns: {
        xs: 'repeat(1, 0.5fr)',
        md: 'repeat(1, 0.3333fr)',
      },
    },
    '&.image-grid-forth': {
      gridTemplateColumns: {
        xs: 'repeat(1, 0.5fr)',
        md: 'repeat(1, 0.25fr)',
      },
    },
    '&.image-grid-2': {
      gridTemplateColumns: 'repeat(2, 1fr)'
    },
    '&.image-grid-3': {
      gridTemplateColumns: {
        xs: 'repeat(2, 1fr)',
        md: 'repeat(3, 1fr)'
      }
    },
    '&.image-grid-4': {
      gridTemplateColumns: {
        xs: 'repeat(2, 1fr)',
        md: 'repeat(4, 1fr)'
      }
    },
    '& .span-2': {
      gridColumn: 'span 2'
    },
    '& .span-4': {
      gridColumn: {
        xs: 'span 2',
        md: 'span 4'
      }
    },
    '& .gatsby-resp-image-wrapper': {
      height: '100%',
      '& img': {
        objectFit: 'cover'
      }
    }
  },
  '& figcaption': {
    typography: 'overline',
    marginBlock: 2.25,
    marginInline: 'auto',
    lineHeight: 'inherit',
    textAlign: 'center',
    fontSize: {
      md: 'button.fontSize',
    }
  },
  '& .gatsby-resp-image-wrapper': {
    width: '100%',
    maxWidth: 'none !important'
  },
  '& .full-width': {
    position: 'relative',
    width: '100vw',
    marginInlineStart: 'calc(50% - 50vw)',
    marginTop: 6,
    '&:not(:last-child)': {
      marginBottom: 7.5
    }
  },
  '& a.anchor svg': {
    fill: theme.palette.text.primary
  },
  '& .MuiAlert-message > p': {
    display: 'contents'
  }
})

export const titleWrapperStyle = () => ({
  ...padding(),
  ...maxWidth,
  marginInline: 'auto',
  marginBlockStart: 7.5,
  marginBlockEnd: 6,
})

export const titleStyle = () => ({
  marginBottom: {
    xs: 3,
    md: 4.5
  },
  typography: {
    xs: 'h4',
    md: 'h3'
  },
  fontVariationSettings: '"WONK" 1'
})


export const infoStyle = () => ({
  display: 'inline-flex',
  gap: '1ch',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  typography: {
    xs: 'body2',
    md: 'body1'
  },
  fontWeight: {
    xs: 'unset',
    md: 'unset',
  },
  fontFamily: {
    xs: 'fontFamilyCaption',
    md: 'fontFamilyCaption',
  },
  marginBottom: {
    xs: 3,
    md: 4.5
  },
})

export const countryStyle = () => ({
  display: 'inline-flex',
  gap: '1ch',
})

export const flagStyle = () => ({
  top: '-0.1em',
})

export const subtitleStyle = theme => ({
  fontStyle: 'italic',
  color: `${theme.palette.text.secondary} !important`
})

export const mainImageWrapperStyle = () => ({
  ...Object.assign(padding(), {
    paddingInline: {
      xs: 0
    }
  }),
  ...maxWidth,
})

export const mainImageStyle = theme => ({
  '&:before, &:after': {
    backgroundAttachment: 'fixed',
    backgroundPosition: 'bottom',
    '@supports (-webkit-overflow-scrolling: touch)': {
      backgroundAttachment: 'scroll'
    }
  },
  position: 'relative',
  height: {
    xs: theme.sizing.mdGridSize * 23,
    md: 0
  },
  paddingBottom: {
    md: '40%',
  },
  overflow: {
    md: 'hidden'
  }
})

export const mainImageInfoStyle = (theme) => {
  const { black } = theme.palette.common

  return {
    position: 'absolute',
    left: 0,
    bottom: 0,
    width: '100%',
    textShadow: `0 2px 2px ${rgba(black, 0.3)}`,
    padding: {
      xs: 2.5,
      lg: 6
    },
    color: 'common.white'
  }
}

export const mainImageInfoBackdropStyle = (theme) => {
  const { black } = theme.palette.common

  return {
    position: 'absolute',
    top: {
      xs: `-${theme.spacing(10)}`,
      md: `-${theme.spacing(5)}`
    },
    left: 0,
    bottom: 0,
    width: '100%',
    backdropFilter: 'blur(40px)',
    mask: `linear-gradient(${rgba(black, 0)} 20%, ${black} 90%)`,
  }
}

export const mainImageInfoContentStyle = () => ({
  position: 'relative',
  zIndex: 1,
})

export const mainImageTitleStyle = () => ({
  textTransform: 'uppercase',
  fontFamily: 'fontFamilyCaption',
  fontWeight: 'unset',
  fontSize: {
    xs: 'h6.fontSize',
    md: 'h5.fontSize'
  }
})

const commonFooterStyle = (theme) => {
  const { paper, default: bgColor } = theme.palette.background
  const { black } = theme.palette.common
  const { borderRadius } = theme.shape

  return {
    ...padding(4.5),
    backgroundImage: `linear-gradient(to bottom, ${bgColor} 0%, ${paper} 100%)`,
    boxShadow: `0 16px 32px ${rgba(black, 0.025)}`,
    borderRadius: {
      xs: `0 0 ${3 * borderRadius}px ${3 * borderRadius}px`,
      md: `0 0 ${4 * borderRadius}px ${4 * borderRadius}px`,
    },
  }
}

export const disqusStyle = theme => ({
  ...commonFooterStyle(theme),
  position: 'relative',
  zIndex: 50,
  '#disqus_thread': {
    ...width(),
    ...maxWidth
  }
})

export const disqusTitleStyle = () => ({
  marginBottom: 6
})

export const bookStyle = theme => ({
  ...commonFooterStyle(theme),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2.5
})

export const bookCoverStyle = (theme) => {
  const { paper } = theme.palette.background
  const { black } = theme.palette.common

  return {
    width: theme.sizing.mdGridSize * 4,
    borderRadius: 1,
    boxShadow: [
      `-4px -4px 4px ${rgba(paper, 0.3)}`,
      `4px 4px 4px ${rgba(black, 0.05)}`,
    ].join(',')
  }
}

export const bookInfoStyle = () => ({
  display: 'flex',
  flexDirection: 'column'
})

export const bookTitleStyle = () => ({
  fontWeight: 'fontWeightMedium',
  lineHeight: 2
})

export const bookMarketplaceStyle = () => ({
  lineHeight: 2
})

export const bookBtnStyle = () => ({
  marginTop: 0.5
})

export const footerStyle = theme => ({
  ...commonFooterStyle(theme),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center'
})

export const flickrBtnStyle = {
  marginBottom: 3
}

export const seriesStyle = () => ({
  marginBottom: 1.5
})

export const prevNextNavStyle = () => ({
  ...padding(null, true),
  ...maxWidth,
  display: 'flex',
  justifyContent: 'space-between'
})

export const dirStyle = () => ({
  maxWidth: '47.5%'
})
